import {
  Box,
  CircularProgress,
  Divider,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import ShowToRole from 'components/Common/Portal/ShowToRole'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useController } from 'customHooks/useController'
import _ from 'lodash'
import React from 'react'
import {
  ResourceMatchType,
  ResourceSummaryDto,
  WorkingStepDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'

type Props = {
  organizationId: string
  projectId: string
  bomItemId: string
  workingStep: WorkingStepDto
  onClose: () => void
  open: boolean
  setResourceName: (resourceName: string) => void
  disabled?: boolean
}

const ResourceSelector = (props: Props) => {
  const [resourceSummaries, setResourceSummaries] = React.useState<
    ResourceSummaryDto[]
  >([])

  const [open, setOpen] = React.useState(false)

  const { controller, loading } = useController(
    () => new BomItemController(props.organizationId, props.projectId)
  )

  React.useEffect(() => {
    const getResourceSummaries = async () => {
      try {
        const resp = await controller.GetBomItemSelectableResources(
          props.bomItemId,
          props.workingStep.primaryWorkingStep
        )
        setResourceSummaries(resp)
      } catch (err) {
        ShowException('project', err)
      }
    }

    if (open && !resourceSummaries.length) {
      getResourceSummaries()
    }
  }, [
    controller,
    open,
    props.bomItemId,
    props.workingStep.primaryWorkingStep,
    resourceSummaries,
  ])

  const saveResource = async (resource: ResourceSummaryDto) => {
    try {
      await controller.SetBomItemResource(
        [props.bomItemId],
        props.workingStep.primaryWorkingStep,
        resource.id
      )
      props.setResourceName(resource.name)
      props.onClose()
    } catch (err) {
      ShowException('project', err)
    }
  }

  const handleClick = (e) => {
    e.preventDefault()
    e.bubble = false

    setOpen((x) => !x)
  }

  if (!props.workingStep.resource?.id) {
    // return <p>&nbsp;</p>
    return null
  }

  if (
    props.workingStep.resource.matchType <=
      ResourceMatchType.OneCapableResource ||
    props.disabled
    // || props.projectId === props.bomItemId
  ) {
    return (
      <ShowToRole seller>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ margin: '0 1em', width: '100%' }}
          // noWrap
        >
          {props.workingStep.resource?.name}
        </Typography>
      </ShowToRole>
    )
  }

  return (
    <ShowToRole seller>
      {props.workingStep.resource?.id && (
        <Select
          value={''}
          native={false}
          open={open}
          fullWidth
          disableUnderline
          onClick={handleClick}
          displayEmpty
          renderValue={() => (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                textAlign: 'right',
                width: '100%',
                whiteSpace: 'pre-line',
              }}
              // noWrap
            >
              {props.workingStep.resource.name}
            </Typography>
          )}
          style={{
            textAlign: 'end',
            margin: '-6px .5em 0 .5em',
            paddingTop: '0',
          }}
        >
          {open ? (
            <span>
              <MenuItem
                value={props.workingStep.resource.id}
                key={props.workingStep.resource.id}
              >
                <Typography variant="body2" color="textSecondary">
                  {props.workingStep.resource.name}
                </Typography>
              </MenuItem>
              <LoadingContainer
                variant="loading-text"
                loading={loading['get-selectable-resources']}
                empty={
                  !resourceSummaries.length && (
                    <Box padding={1}>
                      <Typography variant="body2">
                        no selectable resources
                      </Typography>
                    </Box>
                  )
                }
              >
                {resourceSummaries.map((r, i) => {
                  return (
                    <>
                      <MenuItem
                        key={r.id}
                        selected={r.id === props.workingStep.resource?.id}
                        value={r.id}
                        onClick={(e) => {
                          e.preventDefault()
                          saveResource(r)
                        }}
                      >
                        <ListItemText
                          primary={r.name}
                          secondary={
                            loading[`saving-selected-resource-${r.id}`] && (
                              <span>
                                <CircularProgress disableShrink size={12} />{' '}
                                saving...
                              </span>
                            )
                          }
                        />
                      </MenuItem>
                      {i === resourceSummaries.length - 2 && <Divider />}
                    </>
                  )
                })}
              </LoadingContainer>
            </span>
          ) : null}
        </Select>
      )}
    </ShowToRole>
  )
}

export default React.memo(ResourceSelector, _.isEqual)
